import React, {useState} from 'react';
import {
  Badge,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledTooltip,
  UncontrolledButtonDropdown,
} from 'reactstrap';
import {Modal, ModalBody, ModalFooter, ModalHeader, Spinner} from 'reactstrap';

import PropTypes from 'prop-types';
import {noop, drop, isEmpty, take, truncate} from 'lodash';

import {IconFA} from 'components/Icons';
import {InputTags} from 'components/Inputs';
import {FormSubmit} from 'components/Forms';
import {BaseButton} from 'components/Buttons';

SelectTags.propTypes = {
  action: PropTypes.func,
  name: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.object),
};

SelectTags.defaultProps = {
  data: [],
  action: noop,
};

function SelectTagsModal({
  name,
  data,
  isOpen,
  onSubmit,
  onToggle,
  isSubmitting,
}) {
  function handleToggle() {
    if (!isSubmitting) onToggle();
  }

  return (
    <Modal centered isOpen={isOpen} onToggle={handleToggle}>
      <ModalHeader toggle={handleToggle}>Edit Tags</ModalHeader>

      <FormSubmit isSubmitting={isSubmitting} onSubmit={onSubmit}>
        <ModalBody style={{paddingTop: '2.5rem', paddingBottom: '2.5rem'}}>
          <p>Choose from previously used tags or create a new one.</p>

          <InputTags name={name} options={data} isDisabled={isSubmitting} />
        </ModalBody>

        <ModalFooter>
          <BaseButton
            outline
            className="mr-2"
            onClick={handleToggle}
            disabled={isSubmitting}
          >
            Cancel
          </BaseButton>

          <BaseButton type="submit">
            {isSubmitting ? <Spinner size="sm" /> : 'Save'}
          </BaseButton>
        </ModalFooter>
      </FormSubmit>
    </Modal>
  );
}

function SelectTags({name, data, action, id}) {
  const [isOpen, setIsOpen] = useState(false);
  const handleOpen = () => setIsOpen(!isOpen);

  const {isLoading, onSubmit} = action();

  const top5 = take(data, 5);
  const otherTags = drop(data, 5);

  return (
    <>
      <UncontrolledButtonDropdown down="down">
        <div className="d-flex align-items-center flex-wrap" style={{gap: 3}}>
          {(top5 || []).map((item, index) => {
            const {name} = item || {};
            return (
              <Badge
                className="bg-light text-muted font-weight-normal"
                key={index}
              >
                {truncate(name, {length: 20})}
              </Badge>
            );
          })}

          <div>
            {!!otherTags.length && (
              <DropdownToggle
                color="white"
                className="p-0 m-0 text-primary small ml-1"
                size="sm"
                outline
              >
                <small>+{otherTags.length} more</small>
              </DropdownToggle>
            )}

            {isEmpty(data) ? (
              <BaseButton icon="plus" outline onClick={handleOpen} size="sm">
                Add Tags
              </BaseButton>
            ) : (
              <>
                <IconFA
                  name="edit"
                  id={`edit-tags-${id}`}
                  onClick={handleOpen}
                  style={{cursor: 'pointer'}}
                  className="text-primary ml-1"
                />

                <UncontrolledTooltip target={`edit-tags-${id}`} placement="top">
                  Edit Tags
                </UncontrolledTooltip>
              </>
            )}
          </div>
        </div>

        <DropdownMenu
          className="p-2 border"
          container="body"
          persist
          style={{
            overflow: 'auto',
            maxHeight: '200px',
          }}
        >
          {otherTags.map((item, index) => {
            const {name} = item || {};
            return (
              <DropdownItem
                key={index}
                tag="small"
                className="m-0 p-2 text-left"
              >
                {name}
              </DropdownItem>
            );
          })}
        </DropdownMenu>
      </UncontrolledButtonDropdown>

      <SelectTagsModal
        name={name}
        data={data}
        isOpen={isOpen}
        onSubmit={onSubmit}
        onToggle={handleOpen}
        isSubmitting={isLoading}
      />
    </>
  );
}

export default SelectTags;
